import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import dayjs from 'dayjs'

import type { Address } from '@/models/Address.ts'
import type { Patch } from '@/models/normalize.ts'
import type { PersonInfoResp, PersonTitle as PersonTitleEnum } from '@/models/openapi.ts'

/** UI model for person info. Extends the API `PersonInfoResp` with a parsed `address` object. */
export type PersonInfo = Patch<PersonInfoResp, { address?: Address }>

export type { PersonTitleEnum as PersonTitle }

type TranslateFn = (descriptor: MessageDescriptor) => string

const PERSON_TITLE_MESSAGES: Record<PersonTitleEnum, MessageDescriptor> = {
  MR: msg`Mr`,
  MRS: msg`Mrs`,
  MISS: msg`Miss`,
  MS: msg`Ms`,
  DR: msg`Dr`,
  PROF: msg`Prof`,
  REV: msg`Rev`,
  SIR: msg`Sir`,
  LADY: msg`Lady`,
  DAME: msg`Dame`,
  LORD: msg`Lord`,
  HON: msg`Hon`,
  CAPT: msg`Capt`,
  SGT: msg`Sgt`,
  MAJ: msg`Maj`,
}

export function getPersonInfoFullName(personInfo?: {
  firstName?: string
  lastName?: string
  fullName?: string
}) {
  if (!personInfo) {
    return ''
  }
  if (personInfo.fullName) {
    return personInfo.fullName
  }
  return `${personInfo.firstName ?? ''} ${personInfo.lastName ?? ''}`.trim()
}

export function divideFullName(fullName: string) {
  const names = fullName.split(' ')
  return {
    firstName: names[0],
    lastName: fullName.replace(names[0], '').trim(),
  }
}

export function computeAge(personInfo: { dateOfBirth?: string | null }) {
  if (!personInfo.dateOfBirth) return 0
  return dayjs().diff(dayjs(personInfo.dateOfBirth), 'year')
}

export function formatGenderLabel(gender: string | null | undefined, t: TranslateFn) {
  if (!gender) return t(msg`N/A`)
  const key = gender.toLowerCase()
  if (key === 'male') return t(msg`Male`)
  if (key === 'female') return t(msg`Female`)
  return t(msg`Prefer Not to Say`)
}

/** Convert enum title (e.g. MR) to localized display label (e.g. Mr). */
export function formatPersonTitle(title: string | null | undefined, t: TranslateFn) {
  if (!title) return ''
  const key = title.toUpperCase() as PersonTitleEnum
  const descriptor = PERSON_TITLE_MESSAGES[key]
  if (descriptor) return t(descriptor)
  return title.charAt(0).toUpperCase() + title.slice(1).toLowerCase()
}
